/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, Fragment, useMemo, useCallback, useState, useEffect } from "react";

// antd:
import { Form, Input, AutoComplete, Button, Space, InputNumber } from "antd";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// My Components:
import AppIcons from "../react/AppIcons.tsx";
import DivE from "../react/DivE.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts";
// Reducers:
import { getLng, getDict, getTheme, getCompact, getAppState, changeAppState, getUser, getSafePtr } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

// const { Option } = Select;
const { rz } = window;

type RequiredMark = boolean | "optional" | "customize";

type Tinput = {
  type:"input" | "textarea" | "search" | "number" | "password",
  size:"large" | "middle" | "small",
  autoSize:{minRows:number, maxRows:number} | undefined,
  allowClear:boolean,
  variant:"outlined" | "borderless" | "filled",
  autocomplete?:boolean,
  status?:"error" | "warning",
  maxLength?:number,
  required?:RequiredMark,
  rules?:Array<{[key:string]:any}>,
  enterWithCtrlToSend?:boolean
};

const RZInput:FC<TuniversalElementProps> = ({attributes, children}) => {
  RZInput.displayName = "TextInput";
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const dct = useAppSelector(getDict);
  const lng = useAppSelector(getLng);
  const theme = useAppSelector(getTheme);
  const compact = useAppSelector(getCompact);
  const safe = useAppSelector(getSafePtr);
  const rlng = (lng === "en" ? "ru" : "en");

  const {rootAttributes, key, fullPath, type, size, width, rows, cols, columns, status} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const appState = useAppSelector(getAppState(rootAttributes.key));
  const elState = appState[fullPath];
  const {icon, searchIcon, color, backgroundColor, buttons, min, max, step} = elState.attributes;
  const rootNamespace = useMemo(() => (window[rootAttributes.key || rz.strMakeID(24)]), [rootAttributes.key]);
  const isParentForm = useMemo(() => {
    const parentPath = rz.strParentFolderName(fullPath || ""), parentState = appState[parentPath];
    return Boolean(parentState.name === "form");
  }, [appState, fullPath]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const inputType = useMemo<Tinput>(() => {
    const typ:Tinput = {type: "input", size: "middle", allowClear: false, variant: "outlined", autoSize: undefined, required: false, rules: []};
    (type || "").replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt) => {
      if (~["small", "middle", "large"].indexOf(tt)) { typ.size = tt }
      else if (tt === "autosize") { typ.autoSize = { minRows: 2, maxRows: 6 } }
      else if (tt === "autocomplete") { typ.autocomplete = true }
      else if (tt === "required") { // <input type="small required" size="large" title="..." /> - Образец использования
        typ.required = true;
        typ.rules = [{required: true, message: (attributes["requiredText_" + lng] || attributes.requiredText || attributes["requiredText_" + rlng] || "") || "Required field!"}];
      }
      else if (~["input", "textarea", "search", "number", "password"].indexOf(tt)) { typ.type = tt }
      else if (~["allowClear", "allowclear", "clearButton", "clearbutton"].indexOf(tt)) { typ.allowClear = true }
      else if (~["noborders", "borderless"].indexOf(tt)) { typ.variant = "borderless" }
      else if (~["filled"].indexOf(tt)) { typ.variant = "filled" }
      else if (~["error", "warning"].indexOf(tt)) { typ.status = tt }
      else if (tt === "enterWithCtrlToSend") { typ.enterWithCtrlToSend = true }
    });

    if (attributes?.size) { typ.size = attributes.size }
    if (attributes?.variant) { typ.variant = attributes.variant }
    if (attributes?.allowclear || attributes?.allowClear) { typ.allowClear = ((attributes?.allowclear || attributes?.allowClear) === "true") }

    if (attributes?.autosize === "true") { typ.autoSize = { minRows: 2, maxRows: 6 } }
    else if ((attributes?.autosize || "").startsWith("{") && (attributes?.autosize || "").endsWith("}")) {
      const jsnb = (rz.strReplaceAll(attributes.autosize, "'", "\"")).trim(); let jsn; try { jsn = JSON.parse(jsnb) } catch (err) { console.log(err) }
      typ.autoSize = { minRows: jsn?.minRows || jsn?.minrows || 2, maxRows: jsn?.maxRows || jsn?.maxrows || 6 }
    }

    if (attributes?.textarea === "true") { typ.type = "textarea" }
    if (attributes?.search === "true") { typ.type = "search" }
    if (attributes?.input === "true") { typ.type = "input" }
    if (attributes?.number === "true") { typ.type = "number" }
    if (attributes?.password === "true") { typ.type = "password" }
    if (attributes?.autocomplete === "true") { typ.autocomplete = true }
    if (attributes?.maxLength || attributes?.maxlength) { typ.maxLength = Number(attributes.maxLength || attributes.maxlength) }
    if (attributes?.required) {
      if (attributes.required.startsWith("{") && attributes.required.endsWith("}")) {
        const jsnb = (rz.strReplaceAll(attributes.required, ["'"], ["\""])).trim();
        // console.log(jsnb);
        let jsn; try { jsn = JSON.parse(jsnb) } catch (err) { console.log(err) }
        if (jsn) {
          const rules = jsn[rz.objKeys(jsn)[0]];
          if (rules && rz.isArray(rules)) {
            rules.forEach(((rr) => {
              const rt = attributes["requiredtext_" + lng] || attributes["requiredText_" + lng] || attributes["requiredText_" + rlng];
              if (rt && !rr.message) { rr.message = rt }
              if (rr.pattern) { const rg = rz.strToRegExp(rr.pattern, true); if (rg) { rr.pattern = rg } }
            }));
            typ.required = true; typ.rules = rules;
          }
        }
      }
      else {
        typ.required = (attributes.required === "true");
        if (typ.required) typ.rules = [{required: true, message: (attributes["requiredText_" + lng] || attributes.requiredText || attributes["requiredText_" + rlng] || "") || "Required field!"}];
      }
    }
    if (attributes?.enterWithCtrlToSend) { typ.enterWithCtrlToSend = (attributes.enterWithCtrlToSend === "true") }

    return typ;
  }, [type, attributes, lng, rlng]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    // if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    return userClss;
  }, [elState?.userClass]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    let clr:string; const allc = (color || "").split("|"); if (allc.length > 1) { clr = (theme === "dark" ? allc[1] : allc[0]) } else { clr = allc[0] }
    if (!est?.color && clr) { est.color = clr }
    let bclr:string; const allb = (backgroundColor || "").split("|"); if (allb.length > 1) { bclr = (theme === "dark" ? allb[1] : allb[0]) } else { bclr = allb[0] }
    if (!est?.backgroundColor && bclr) { est.backgroundColor = bclr }
    return est;
  }, [elState?.userStyle, elState?.visible, color, backgroundColor, theme, permissions]);

  // eslint-disable-next-line react-hooks/exhaustive-deps, max-len
  const title = useMemo<string | undefined>(() => (elState.attributes["title_" + lng] || elState.attributes.title || elState.attributes["title_" + rlng] || attributes["title_" + lng] || attributes.title || attributes["title_" + rlng]), [elState.attributes, attributes, lng]);
  // eslint-disable-next-line max-len, react-hooks/exhaustive-deps
  const title2 = useMemo<string | undefined>(() => (elState?.attributes["title2_" + lng] || elState?.attributes.title2 || elState?.attributes["title2_" + rlng] || attributes["title2_" + lng] || attributes.title2 || attributes["title2_" + rlng]), [elState?.attributes, attributes, lng]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tooltip = useMemo<string | undefined>(() => (elState.attributes["tooltip_" + lng] || elState.attributes.tooltip || elState.attributes["tooltip_" + rlng]), [elState.attributes, lng]);

  const items = useMemo(() => {
    let options:Array<{value:string}> = [];
    if (elState?.items) {
      if (rz.isArray(elState.items) && elState.items.length) {
        if (!elState.items[0].value) { options = elState.items.map((ii) => ({value: ii})) } else { return elState.items }
      }
      else if (rz.isObject(elState.items) && elState.items.items && elState.items.items.length) { return elState.items.items }
    }
    return options;
  }, [elState?.items]);

  const srchIcon = useMemo(() => {
    let ico:React.ReactElement | undefined;
    if (searchIcon) { ico = (<AppIcons key={key + "-searchicon"} icopath={searchIcon} attrs={{}} />) }
    else { ico = (<AppIcons key={key + "-searchicon"} icopath="ai/AiOutlineSearch" attrs={{}} />) }
    return (
      <span style={{minWidth: "3em"}}>
        {ico}
      </span>
    );
  }, [key, searchIcon]);

  const [myValue, setMyValue] = useState<string | number | undefined>();
  useEffect(() => {
    if (elState?.value !== myValue) { setMyValue(elState?.value) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elState?.value]);

  const onPressEnter = useCallback((event:React.KeyboardEvent<HTMLInputElement | HTMLFormElement | HTMLTextAreaElement>) => {
    // @ts-ignore
    const {value} = event.target;
    const {ctrlKey, shiftKey, altKey, metaKey, nativeEvent} = event;
    const isComposing = Boolean((nativeEvent as KeyboardEvent | undefined)?.isComposing);
    const wantsCtrlToSend = Boolean(inputType.enterWithCtrlToSend);
    const isTextarea = (inputType.type === "textarea");
    const shouldSubmit = (wantsCtrlToSend ? ctrlKey : (isTextarea ? (!shiftKey && !altKey && !metaKey && !ctrlKey) : true));

    if (isComposing || !shouldSubmit) { return }

    if (isTextarea || wantsCtrlToSend) { event.preventDefault() }
    if (fnc.config?.isDev) console.log({action: "<Input key='" + key + "'> -> onPressEnter()", value});
    // @ts-ignore
    dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {value}}));

    if (rootNamespace && (attributes.onPressEnter || attributes.onpressenter) && rootNamespace[attributes.onPressEnter || attributes.onpressenter]) {
      const clientFNC = rootNamespace[attributes.onPressEnter || attributes.onpressenter];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [inputType.enterWithCtrlToSend, inputType.type, key, dispatch, rootAttributes.key, fullPath, rootNamespace, attributes, user, lng, dct, safe]);

  const onSearch = useCallback((value) => {
    if (fnc.config?.isDev) console.log({action: "<Input key='" + key + "'> -> onSearch()", value});
    // @ts-ignore
    dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {value}}));

    if (rootNamespace && (attributes.onSearch || attributes.onsearch) && rootNamespace[attributes.onSearch || attributes.onsearch]) {
      const clientFNC = rootNamespace[attributes.onSearch || attributes.onsearch];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [key, dispatch, rootAttributes.key, fullPath, rootNamespace, attributes, user, lng, dct, safe]);

  const onChange = useCallback((event:React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const inputValue = (inputType.type === "number" ? event : event.target.value);
    // if (fnc.config?.isDev) console.log({action: "<Input key='" + key + "'> -> onChange('" + key + "')", value: inputValue});
    // @ts-ignore
    if (inputValue !== myValue) { setMyValue(inputValue) }
    // dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {value: inputValue}}));
    if (rootNamespace && (attributes.onChange || attributes.onchange) && rootNamespace[attributes.onChange || attributes.onchange]) {
      const clientFNC = rootNamespace[attributes.onChange || attributes.onchange];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value: inputValue, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value: inputValue, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputType.type, key, rootNamespace, attributes, rootAttributes.key, user, dct, dispatch, safe]);

  const onBlur = useCallback((event:React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const inputValue = (inputType.type === "number" ? Number(event.target.value) : event.target.value);
    if (fnc.config?.isDev) console.log({action: "<Input key='" + key + "'> -> onBlur('" + key + "')", value: inputValue});
    // @ts-ignore
    dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {value: inputValue}}));

    if (rootNamespace && (attributes.onBlur || attributes.onblur) && rootNamespace[attributes.onBlur || attributes.onblur]) {
      const clientFNC = rootNamespace[attributes.onBlur || attributes.onblur];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value: inputValue, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value: inputValue, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [inputType.type, key, dispatch, rootAttributes.key, fullPath, rootNamespace, attributes, user, lng, dct, safe]);

  const miniButtonClick = useCallback((bkey:string, funcName:string) => { // event:React.MouseEvent<HTMLElement>
    const {value} = appState[fullPath];
    if (fnc.config?.isDev) console.log({action: "<Input key='" + key + "'> -> miniButtonClick('" + bkey + "') -> " + funcName + "()", value, funcName});
    // @ts-ignore
    // dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {value}}));
    if (rootNamespace && (funcName || attributes.miniButtonClick || attributes.minibuttonclick) && rootNamespace[funcName || attributes.miniButtonClick || attributes.minibuttonclick]) {
      const clientFNC = rootNamespace[funcName || attributes.miniButtonClick || attributes.minibuttonclick];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, bkey, value, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, bkey, value, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [appState, fullPath, key, rootNamespace, attributes, rootAttributes.key, user, lng, dct, dispatch, safe]);

  const acOnSearch = useCallback((value:string) => {
    if (fnc.config?.isDev) console.log({action: "<Autocomplete key='" + key + "'> -> autocompleteOnSearch()", value});
    setMyValue(value);
    // @ts-ignore
    dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {value}}));

    if (rootNamespace && attributes?.autocompleteOnSearch && rootNamespace[attributes.autocompleteOnSearch]) {
      const clientFNC = rootNamespace[attributes?.autocompleteOnSearch];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [key, dispatch, rootAttributes.key, fullPath, rootNamespace, attributes, user, lng, dct, safe]);

  const acOnSelect = useCallback((value:string) => {
    if (fnc.config?.isDev) console.log({action: "<Autocomplete key='" + key + "'> -> autocompleteOnSelect()", value});
    setMyValue(value);
    // @ts-ignore
    dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {value}}));
    if (rootNamespace && attributes?.autocompleteOnSelect && rootNamespace[attributes?.autocompleteOnSelect]) {
      const clientFNC = rootNamespace[attributes?.autocompleteOnSelect];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [key, dispatch, rootAttributes.key, fullPath, rootNamespace, attributes, user, lng, dct, safe]);

  const filterOption = useCallback((inputValue:string, option:any) => {
    let item = (rz.isObject(option) ? (option.title || option.label || option.value || option.key) : (option || ""));
    if (rz.isObject(item) && item.props && item.props.children && item.props.children.length) { // If we have a ReactElement inside 'option'
      const chlds = item.props.children.filter((ch) => (ch && ch.props && ch.props.children && rz.isString(ch.props.children))).map((ch) => (ch.props.children));
      if (chlds && chlds.length) { item = chlds[0] }
    }
    return rz.strCompare(item, inputValue, ".caseNoSensitive.toLowerCase.trim.substring");
  }, []);

  const wrapperAutocomplete = useCallback((mainComponent:React.ReactNode) => {
    if (inputType.autocomplete) {
      return (
        <AutoComplete
          key={key + "-autocomplete"}
          options={items}
          value={myValue}
          defaultValue={elState.value}
          disabled={elState?.disabled || permissions.disabled === true}
          onSelect={acOnSelect}
          onSearch={acOnSearch}
          filterOption={filterOption}
          style={(elState?.visible === false || permissions.visible === false) ? {display: "none"} : undefined}
        >
          {mainComponent}
        </AutoComplete>
      )
    }
    return mainComponent;
  }, [inputType.autocomplete, key, items, myValue, elState.value, elState?.disabled, elState?.visible, permissions.disabled, permissions.visible, acOnSelect, acOnSearch, filterOption]);

  const wrapperForm = useCallback((mainComponent:React.ReactNode) => {
    if (isParentForm) {
      const frmItemProps:{[key:string]:any} = {
        key: key + "-formitem", label: (<span className="rz-txt-std">{title2}</span>), className: "rz-width-expand",
        style: ((elState?.visible === false || permissions.visible === false) ? {display: "none"} : undefined)
      };
      if (inputType.rules) { frmItemProps.name = String(key); frmItemProps.rules = inputType.rules; frmItemProps.getValueProps = () => (elState.value) }
      return (
        <Form.Item {...frmItemProps}>
          <div>{wrapperAutocomplete(mainComponent)}</div>
        </Form.Item>
      )
    }
    return wrapperAutocomplete(mainComponent);
  }, [isParentForm, wrapperAutocomplete, title2, key, inputType.rules, elState?.visible, elState.value, permissions.visible]);

  const subStyle = useMemo(() => {
    const smp = {padding: 0, marginLeft: -11, marginRight: -11, height: 30, iconWidth: 20, fontSize: "1.25em"};
    if (compact) { smp.marginLeft = -7; smp.marginRight = -7 }
    switch (inputType.size) {
      case "small": smp.height = (compact ? 20 : 22); smp.iconWidth = 14; break;
      case "large": smp.height = (compact ? 34 : 39); smp.iconWidth = 16; break;
      default: smp.height = (compact ? 28 : 30); smp.iconWidth = 14;
    }
    return smp;
  }, [compact, inputType.size]);

  // Input Icon
  const AdditionalIcon = (jsna, suffix) => {
    if (!jsna || !rz.isString(jsna)) { return undefined }
    const jsnb = (rz.strReplaceAll(jsna, "'", "\"")).trim(); let jsn; try { jsn = JSON.parse(jsnb) } catch (err) { console.log(err) }
    if (!jsn || !jsn.icon || (suffix === "-icopostfix" && ~["before", "left"].indexOf(jsn.position)) || (suffix === "-icoprefix" && (!jsn.position || ~["after", "right"].indexOf(jsn.position)))) { return undefined }
    return (
      <DivE key={key + suffix + "-dive"}
        className="uk-flex uk-flex-middle"
        style={{width: subStyle.iconWidth}}
        uk-tooltip={(jsn["tooltip_" + lng] || jsn.tooltip || jsn["tooltip_" + rlng] ? jsn["tooltip_" + lng] || jsn.tooltip || jsn["tooltip_" + rlng] : undefined)}
      >
          <AppIcons
            key={key + suffix + "-dive-icon"}
            icopath={jsn.icon}
            style={{width: subStyle.iconWidth}}
            attrs={{ color: jsn?.color || "black|white", className: jsn?.class || "" }}
          />
      </DivE>
    );
  };

  // Additional buttons (before / after)
  const AdditionalButtons = useCallback((jsna, suffix) => {
    if (!jsna || !rz.isString(jsna)) { return undefined }
    const jsnb = (rz.strReplaceAll(jsna, "'", "\"")).trim(); let jsn; try { jsn = JSON.parse(jsnb) } catch (err) { console.log(err) }
    if (!jsn || !jsn.elements || (suffix === "-postfix" && ~["before", "left"].indexOf(jsn.position)) || (suffix === "-prefix" && (!jsn.position || ~["after", "right"].indexOf(jsn.position)))) { return undefined }
    const isPostfix = (suffix === "-postfix");
    return (
      <Space.Compact key={key + suffix} block style={{padding: subStyle.padding, marginLeft: subStyle.marginLeft, marginRight: subStyle.marginRight, height: subStyle.height}}>
        {jsn.elements.map((el, i) => {
          let ReactEl = (
            <Button key={el.key || (key + suffix + "-button-" + i)}
              type={el.type || "primary"} size={size || "middle"}
              style={{
                minWidth: "4em",
                paddingTop: 0,
                paddingBottom: 0,
                height: subStyle.height,
                fontSize: subStyle.fontSize,
                backgroundColor: el?.bgColor,
                // скругление зависит от стороны кнопка должна "стыковаться" с инпутом и слева, и справа.
                borderTopLeftRadius: (isPostfix ? "0" : undefined),
                borderBottomLeftRadius: (isPostfix ? "0" : undefined),
                borderTopRightRadius: (!isPostfix ? "0" : undefined),
                borderBottomRightRadius: (!isPostfix ? "0" : undefined)
              }}
              disabled={elState?.disabled || permissions.disabled === true}
              onClick={() => { miniButtonClick(el.key || (key + suffix + "-button-" + i), (el.onClick || el.onclick || "")) }}
            >
              <span key={key + suffix + "-button-span1-" + i} className="uk-flex uk-flex-middle" style={{width: subStyle.iconWidth}}>
                {(el.icon
                  ? <AppIcons
                      key={key + suffix + "-button-icon-" + i}
                      icopath={el.icon}
                      style={{width: subStyle.iconWidth}}
                      attrs={{ color: el?.iconColor, bgcolor: el?.bgColor, className: el?.iconClass || "", size: el?.iconSize }}
                    />
                  : undefined
                )}
              </span>
              <span key={key + suffix + "-button-span2-" + i}>{el["title_" + lng] || el.title || el["title_" + rlng] || ""}</span>
            </Button>
          );
          if (el.tooltip || el.tooltip_en || el.tooltip_ru) {
            ReactEl = (
              <DivE key={key + suffix + "-dive-tooltip-" + i}
                uk-tooltip={(el["tooltip_" + lng] || el.tooltip || el["tooltip_" + rlng] ? el["tooltip_" + lng] || el.tooltip || el["tooltip_" + rlng] : undefined)}>
                {ReactEl}
              </DivE>);
          }
          return ReactEl;
        })}
      </Space.Compact>
    );
  }, [key, subStyle, size, lng, rlng, miniButtonClick, elState?.disabled, permissions.disabled]);

  if (inputType.type === "textarea") {
    return wrapperForm(
      <Input.TextArea
        key={key} id={key} style={styl} className={clsu.join(" ")} uk-tooltip={tooltip}
        value={myValue}
        defaultValue={elState.value}
        placeholder={(attributes["placeholder_" + lng] || attributes.placeholder || attributes["placeholder_" + rlng] || "")}
        allowClear={inputType.allowClear}
        variant={inputType.variant}
        size={inputType.size}
        status={elState?.status || status || inputType.status}
        autoSize={inputType.autoSize}
        rows={!inputType.autoSize && !rows ? undefined : (rz.isNumber(rows) ? rows : rz.strSearchNum(rows, 1, 0, true))}
        cols={!inputType.autoSize && !(cols || columns) ? undefined : (rz.isNumber(cols || columns) ? (cols || columns) : rz.strSearchNum((cols || columns), 1, 0, true))}
        onChange={onChange}
        onBlur={onBlur}
        onPressEnter={onPressEnter}
        // maxLength={inputType.maxLength}
        disabled={elState?.disabled || permissions.disabled === true}
        hidden={elState?.visible === false || permissions.visible === false}
      />
    );
  }

  if (inputType.type === "search") {
    return wrapperForm(
      <Input.Search
        key={key} id={key} style={styl} className={clsu.join(" ")} uk-tooltip={tooltip}
        value={myValue}
        defaultValue={elState.value}
        placeholder={(attributes["placeholder_" + lng] || attributes.placeholder || attributes["placeholder_" + rlng] || "")}
        allowClear={inputType.allowClear}
        variant={inputType.variant}
        size={inputType.size}
        status={inputType.status}
        onChange={onChange}
        onPressEnter={onPressEnter}
        onSearch={onSearch}
        onBlur={onBlur}
        enterButton={srchIcon}
        width={width || "100%"}
        // maxLength={inputType.maxLength}
        prefix={(icon ? AdditionalIcon(icon, "-icoprefix") : undefined)}
        suffix={(icon ? AdditionalIcon(icon, "-icopostfix") : undefined)}
        addonBefore={buttons ? AdditionalButtons(buttons, "-prefix") : undefined}
        addonAfter={buttons ? AdditionalButtons(buttons, "-postfix") : undefined}
        loading={elState?.loading}
        disabled={elState?.disabled || permissions.disabled === true}
        hidden={elState?.visible === false || permissions.visible === false}
      />
    );
  }

  if (inputType.type === "number") {
    return wrapperForm(
      <InputNumber
        key={key} id={key} style={styl} className={clsu.join(" ")} uk-tooltip={tooltip}
        value={(rz.isNumber(myValue) ? myValue : (rz.isString(myValue) ? Number(myValue) : undefined))}
        defaultValue={(rz.isNumber(elState.value) ? elState.value : (rz.isString(elState.value) ? Number(elState.value) : undefined))}
        placeholder={(attributes["placeholder_" + lng] || attributes.placeholder || attributes["placeholder_" + rlng] || "")}
        variant={inputType.variant}
        size={inputType.size}
        status={inputType.status}
        min={(rz.isNumber(min) ? min : (rz.isString(min) ? Number(min) : undefined))}
        max={(rz.isNumber(max) ? max : (rz.isString(max) ? Number(max) : undefined))}
        step={(rz.isNumber(step) ? step : (rz.isString(step) ? Number(step) : undefined))}
        onChange={onChange}
        onPressEnter={onPressEnter}
        onBlur={onBlur}
        width={width || "100%"}
        // maxLength={inputType.maxLength}
        prefix={(icon ? AdditionalIcon(icon, "-icoprefix") : undefined)}
        suffix={(icon ? AdditionalIcon(icon, "-icopostfix") : undefined)}
        addonBefore={buttons ? AdditionalButtons(buttons, "-prefix") : undefined}
        addonAfter={buttons ? AdditionalButtons(buttons, "-postfix") : undefined}
        disabled={elState?.disabled || permissions.disabled === true}
        hidden={elState?.visible === false || permissions.visible === false}
      />
    );
  }

  if (inputType.type === "password") {
    return wrapperForm(
      <Input.Password
        key={key} id={key} style={styl} className={clsu.join(" ")} uk-tooltip={tooltip}
        iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
        value={myValue}
        defaultValue={elState.value}
        placeholder={(attributes["placeholder_" + lng] || attributes.placeholder || attributes["placeholder_" + rlng] || "")}
        allowClear={inputType.allowClear}
        variant={inputType.variant}
        size={inputType.size}
        status={inputType.status}
        onChange={onChange}
        onPressEnter={onPressEnter}
        onBlur={onBlur}
        width={width || "100%"}
        // maxLength={inputType.maxLength}
        prefix={(icon ? AdditionalIcon(icon, "-icoprefix") : undefined)}
        suffix={(icon ? AdditionalIcon(icon, "-icopostfix") : undefined)}
        addonBefore={buttons ? AdditionalButtons(buttons, "-prefix") : undefined}
        addonAfter={buttons ? AdditionalButtons(buttons, "-postfix") : undefined}
        disabled={elState?.disabled || permissions.disabled === true}
        hidden={elState?.visible === false || permissions.visible === false}
      />
    );
  }

  // console.log(elState.value);

  return wrapperForm(
    <Input
      key={key} id={key} style={styl} className={clsu.join(" ")} uk-tooltip={tooltip}
      value={myValue}
      defaultValue={elState.value}
      placeholder={(attributes["placeholder_" + lng] || attributes.placeholder || attributes["placeholder_" + rlng] || "")}
      allowClear={inputType.allowClear}
      variant={inputType.variant}
      size={inputType.size}
      status={inputType.status}
      onChange={onChange}
      onPressEnter={onPressEnter}
      onBlur={onBlur}
      width={width || "100%"}
      // maxLength={inputType.maxLength}
      prefix={(icon ? AdditionalIcon(icon, "-icoprefix") : undefined)}
      suffix={(icon ? AdditionalIcon(icon, "-icopostfix") : undefined)}
      addonBefore={buttons ? AdditionalButtons(buttons, "-prefix") : undefined}
      addonAfter={buttons ? AdditionalButtons(buttons, "-postfix") : undefined}
      disabled={elState?.disabled || permissions.disabled === true}
      hidden={elState?.visible === false || permissions.visible === false}
    />
  );
}

export default RZInput;
/* eslint-enable @typescript-eslint/no-unused-vars */
